/**
 * Global utilities and helpers for the Shopify theme
 * Provides focus management, media controls, form helpers, and Shopify API utilities
 * Also includes back-to-top functionality and scroll progress tracking
 */

/**
 * Gets all focusable elements within a container
 * @param {HTMLElement} container - The container to search within
 * @returns {Array} Array of focusable elements
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};


/**
 * Traps focus within a container for accessibility (modal dialogs, etc.)
 * @param {HTMLElement} container - The container to trap focus within
 * @param {HTMLElement} elementToFocus - Element to focus initially (defaults to container)
 */
function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event?.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

/**
 * Pauses all media elements on the page (videos, YouTube, Vimeo, 3D models)
 * Used when opening modals or switching contexts
 */
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause());
}

/**
 * Removes focus trap and restores normal tab behavior
 * @param {HTMLElement|null} elementToFocus - Optional element to focus after removing trap
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}


/**
 * Debounces function calls to prevent excessive execution
 * @param {Function} fn - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Serializes form data into JSON string
 * @param {HTMLFormElement} form - Form element to serialize
 * @returns {string} JSON string of form data
 */
const serializeForm = form => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

/**
 * Creates fetch configuration object for API requests
 * @param {string} type - Response type (json, javascript, etc.)
 * @returns {Object} Fetch configuration object
 */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

/**
 * Gets the index of a node among its siblings
 * @param {HTMLElement} node - The node to find index for
 * @returns {number} Index of the node
 */
function getNodeIndex(node) {
  return Array.from(node.parentNode.children).indexOf(node);
}

// back to top button
document.querySelectorAll('[data-back-to-top]').forEach(($button) => {
  $button.addEventListener('click', (e) => {
    e.preventDefault();
    window.scroll({top: 0, behavior: 'smooth'});
  });
});


/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

/**
 * Binds function to specific scope (this context)
 * @param {Function} fn - Function to bind
 * @param {Object} scope - Scope to bind to
 * @returns {Function} Bound function
 */
Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

/**
 * Sets select element value by matching option value or text
 * @param {HTMLSelectElement} selector - Select element
 * @param {string} value - Value to match
 * @returns {number|undefined} Index of matched option
 */
Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

/**
 * Cross-browser event listener attachment
 * @param {HTMLElement} target - Element to attach listener to
 * @param {string} eventName - Event name
 * @param {Function} callback - Event handler function
 */
Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

/**
 * Creates and submits a form for POST requests (used for links that need POST)
 * @param {string} path - URL to post to
 * @param {Object} options - Options including method and parameters
 */
Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

/**
 * Constructor for country/province selector functionality
 * @param {string} country_domid - ID of country select element
 * @param {string} province_domid - ID of province select element
 * @param {Object} options - Configuration options
 */
Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  /**
   * Initializes country selector with default value
   */
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  /**
   * Initializes province selector with default value
   */
  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  /**
   * Handles country selection change and updates province options
   * @param {Event} e - Change event
   */
  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt2 = document.createElement('option');
        opt2.value = provinces[i][0];
        opt2.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt2);
      }

      this.provinceContainer.style.display = "";
    }
  },

  /**
   * Clears all options from a select element
   * @param {HTMLSelectElement} selector - Select element to clear
   */
  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  /**
   * Sets options for a select element
   * @param {HTMLSelectElement} selector - Select element
   * @param {Array} values - Array of option values
   */
  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};



/**
 * Updates SVG stroke progress based on scroll position
 * Used for scroll progress indicators
 */
function updateStroke() {
  const path = document.querySelector(".path");
  if (!path) return;
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = scrollTop / docHeight; // 0 to 1
    const dashOffset = 1 - (progress * (1 - 0.515)); // Interpolating between 1 and 0.515
    path.style.strokeDashoffset = dashOffset;
}

window.addEventListener("scroll", updateStroke);

window.customerTier = "{{ customer.metafields.loyaltylion.loyalty_tier | default: '' }}";
